import type { ScoreGame } from '@shared/types/game';
import { allSeatStats, buildRoundPlan, catchUpVerdicts } from '@shared/game-logic';
import { Panel } from '@/components/ui';
import LazyStandingsChart from '@/components/LazyStandingsChart';
import { currentRoundOf } from '@/store/scorekeeper';
import { seatColor, signed } from '@/lib/labels';

/**
 * In-game analysis: how the standings moved, who calls their bids accurately,
 * and — once the finish is in sight — who is still mathematically in it.
 */
export default function StatsPanel({ game }: { game: ScoreGame }) {
  const stats = allSeatStats(game.rounds, game.seats.length);
  const scoredRounds = game.rounds.filter((round) => round.status === 'done').length;
  const current = currentRoundOf(game);

  const plan = buildRoundPlan(game.config.roundPlan);
  const verdicts = current
    ? catchUpVerdicts(game.rounds, plan, current.roundNo, game.seats, game.config.scoring)
    : [];
  const eliminated = verdicts.filter((verdict) => !verdict.canStillWin);

  /**
   * Early on, "can everyone still win?" is trivially yes and just adds noise.
   * Show it once the finish is in sight, or the moment somebody is actually out.
   */
  const roundsLeft = plan.length - (current?.roundNo ?? plan.length) + 1;
  const showCatchUp = current !== undefined && (eliminated.length > 0 || roundsLeft <= 4);

  if (scoredRounds === 0) {
    return (
      <Panel title="Statistiek">
        <p className="text-sm text-slate-400">
          Zodra de eerste ronde is afgerond verschijnt hier het verloop van de stand en wie er over-
          of onderbiedt.
        </p>
      </Panel>
    );
  }

  return (
    <div className="space-y-4">
      <Panel title="Verloop van de stand">
        <LazyStandingsChart game={game} />
      </Panel>

      <Panel title="Bied-nauwkeurigheid">
        <ul className="space-y-3">
          {game.seats.map((seat) => {
            const row = stats[seat.seat];
            return (
              <li key={seat.seat}>
                <div className="flex items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: seatColor(seat.seat) }}
                  />
                  <span className="min-w-0 flex-1 truncate font-medium">{seat.displayName}</span>
                  <span className="text-sm tabular-nums text-slate-300">
                    {row.exact}/{row.roundsScored} precies goed
                  </span>
                </div>
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-base-700">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${row.exactPct}%`,
                      backgroundColor: seatColor(seat.seat),
                    }}
                  />
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  {describeIndex(row.overUnderIndex)}
                  {row.bestRound !== null && ` · beste ronde ${signed(row.bestRound)}`}
                  {row.worstRound !== null && `, slechtste ${signed(row.worstRound)}`}
                </p>
              </li>
            );
          })}
        </ul>
      </Panel>

      {showCatchUp && (
        <Panel title="Kan iedereen nog winnen?">
          {eliminated.length === 0 ? (
            <p className="text-sm text-slate-300">
              Ja — er zijn nog {verdicts[0].maxRemaining} punten te verdienen, genoeg voor iedereen
              om de koploper in te halen.
            </p>
          ) : (
            <>
              <p className="text-sm text-slate-300">
                Er {eliminated.length === 1 ? 'is' : 'zijn'} nog {verdicts[0].maxRemaining} punten
                te verdienen. Dat is niet meer genoeg voor{' '}
                {eliminated.map((verdict) => verdict.displayName).join(' en ')} om de koploper te
                bereiken, zelfs met alles goed.
              </p>
              <p className="mt-2 text-xs text-slate-500">
                Gerekend vanaf de huidige stand, met de koploper op nul verdere punten.
              </p>
            </>
          )}
        </Panel>
      )}
    </div>
  );
}

function describeIndex(index: number): string {
  const rounded = Math.round(index * 10) / 10;
  if (Math.abs(rounded) < 0.25) return 'Bod en resultaat lopen gelijk op';
  if (rounded > 0) return `Overbiedt met gemiddeld ${rounded.toFixed(1)} slag per ronde`;
  return `Onderbiedt met gemiddeld ${Math.abs(rounded).toFixed(1)} slag per ronde`;
}
